import { useQuery } from '@tanstack/react-query';
import { Amount } from '@ui/components/Amount/Amount';
import { Copy, ShareLink, X as XIcon } from '@ui/components/icons';
import {
  ShareIdentityControls,
  ShareIdentityRow,
  useShareIdentity,
} from '@ui/components/share/useShareIdentity';
import { basicToaster } from '@ui/components/Toast';
import { Interval } from '@ui/constants/poll';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { Textarea } from '@ui/lib/park/ui/textarea';
import { slvmonQuery } from '@ui/services/backend/hooks/slvmon/slvmonQuery';
import {
  canShareNodeImage,
  copyNodeImageToClipboard,
  downloadNodeAsPng,
  shareNodeImage,
} from '@ui/utils/image/exportNodeAsImage';
import BigNumber from 'bignumber.js';
import { useEffect, useRef, useState } from 'react';
import { Flex, HStack, Stack, styled, VStack } from 'styled-system/jsx';

const APY_DISPLAY_CAP = 1500;

export const StakeShareOverlay = ({
  children,
  stakedAmount,
  ...rest
}: Dialog.RootProps & {
  children: React.ReactNode;
  stakedAmount: BigNumber | null;
}) => {
  const [open, setOpen] = useState(false);
  const [shareText, setShareText] = useState(
    'Earning auto-compounding yield on $LVMON with @LeverUp_xyz',
  );
  const [copied, setCopied] = useState(false);
  // Mobile/in-app browsers can't reliably copy images to the clipboard, but
  // they can open the native share sheet (Save to Photos + share anywhere).
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(canShareNodeImage());
  }, []);
  // Disable the image button while rendering so a half-loaded card can't be
  // saved/copied (image generation waits for all images to finish loading).
  const [imageBusy, setImageBusy] = useState(false);
  const imageBusyRef = useRef(false);
  const shareRef = useRef<HTMLDivElement | null>(null);
  const identity = useShareIdentity();

  const { data: apy } = useQuery({
    queryKey: slvmonQuery.getAverageApyKey({ days: 1 }),
    queryFn: slvmonQuery.getAverageApyFn({ days: 1 }),
    refetchInterval: Interval.SLOW,
  });

  const apyPercentValue = typeof apy === 'number' ? apy * 100 : null;
  const formattedApy =
    apyPercentValue === null
      ? '-'
      : apyPercentValue > APY_DISPLAY_CAP
      ? `> ${APY_DISPLAY_CAP}`
      : apyPercentValue.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const onCopyImage = async () => {
    const node = shareRef.current;
    if (!node || imageBusyRef.current) return;
    imageBusyRef.current = true;
    setImageBusy(true);
    try {
      // Mobile: open the native share sheet (Save to Photos + share anywhere).
      // Clipboard image writes are unreliable on iOS/in-app browsers.
      if (canShare) {
        try {
          await shareNodeImage(node, {
            pixelRatio: 3,
            fileName: 'leverup-share.png',
          });

          return;
        } catch (e) {
          // User dismissed the share sheet — not an error.
          if ((e as { name?: string })?.name === 'AbortError') return;
          // Otherwise fall through to download.
          try {
            await downloadNodeAsPng(node, 'leverup-share.png', {
              pixelRatio: 3,
            });

            return;
          } catch {}
        }
      }

      // Desktop: copy to clipboard.
      try {
        await copyNodeImageToClipboard(node, { pixelRatio: 3 });
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      } catch {
        try {
          basicToaster.error({ title: 'Copy failed, Please try again' });
        } catch {}
      }
    } finally {
      imageBusyRef.current = false;
      setImageBusy(false);
    }
  };

  const onShareX = async () => {
    if (shareRef.current) {
      try {
        await copyNodeImageToClipboard(shareRef.current, { pixelRatio: 3 });
      } catch {
        // allow tweeting without image
      }
    }
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(
      shareText,
    )}`;
    window.open(url, '_blank');
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      basicToaster.success({ title: 'Copied to clipboard' });
    } catch {}
  };

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(d) => setOpen(d.open)}
      {...rest}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="#131314"
          color="greyWhite.lightGrey1"
          display="flex"
          flexDir="column"
          // Cap to the viewport and let the description scroll internally.
          // iOS Safari can't reliably scroll the fixed-position positioner.
          minH={{ base: '0', tablet: 'unset' }}
          maxH={{ base: '100dvh', tablet: '90dvh' }}
          overflow="hidden"
          minW={{ base: 'full', tablet: '500px' }}
          maxW={{ base: 'full', tablet: '500px' }}
          borderRadius="12px"
        >
          <Dialog.Title asChild p={0}>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              px="16px"
              py="12px"
              bg="#131314"
              borderRadius="12px"
            >
              <Text textStyle="headings.large">Share LVMON Staking</Text>
              <Dialog.CloseTrigger boxShadow="none" bg="none">
                <Icon w="20px" h="20px">
                  <XIcon />
                </Icon>
              </Dialog.CloseTrigger>
            </Flex>
          </Dialog.Title>

          <Dialog.Description
            pt={0}
            display="flex"
            flexDir="column"
            gap={3}
            cursor="default"
            flex="1"
            minH="0"
            overflowY="auto"
            css={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              // Keep children at their natural height so the column overflows
              // and scrolls, instead of flexbox squishing them (which clipped
              // the card and prevented scrolling on iOS).
              '& > *': { flexShrink: 0 },
            }}
          >
            {/* Share image */}
            <styled.div
              position="relative"
              className="group"
              w="full"
              h="270px"
              // Hover border via outline (offset inward) so it doesn't eat
              // into the content box.
              outline="1px solid"
              outlineColor="transparent"
              outlineOffset="-1px"
              transition="all 0.15s ease"
              cursor="pointer"
              _hover={{
                outlineColor: 'greyWhite.outline',
                filter: 'brightness(1.03)',
              }}
              onClick={onCopyImage}
            >
              <styled.div
                ref={shareRef}
                position="relative"
                w="full"
                h="full"
                overflow="hidden"
                style={{
                  background: '#0a0a0b',
                  backgroundImage:
                    'radial-gradient(120% 80% at 0% 0%, rgba(204,255,0,0.12), transparent 45%),' +
                    'radial-gradient(120% 90% at 100% 100%, rgba(204,255,0,0.1), transparent 45%),' +
                    'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),' +
                    'linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                  backgroundSize: '100% 100%, 100% 100%, 22px 22px, 22px 22px',
                }}
              >
                {/* LeverUp logo, top-left */}
                <Flex position="absolute" top="14px" left="20px">
                  <styled.img
                    src="/images/logo.svg"
                    alt="LeverUp"
                    width="130px"
                    height="40px"
                  />
                </Flex>

                {/* Stake stats, left (vertically centered below the logo) */}
                <Flex
                  position="absolute"
                  left="24px"
                  top="52px"
                  bottom="16px"
                  maxW="280px"
                  direction="column"
                  justifyContent="center"
                  gap="16px"
                  alignItems="flex-start"
                >
                  <ShareIdentityRow identity={identity} maxW="full" />

                  <VStack alignItems="flex-start" gap="2px">
                    <HStack gap="5px" alignItems="center">
                      <Text
                        textStyle="text.xsmall"
                        color="greyWhite.lightGrey4"
                        whiteSpace="nowrap"
                      >
                        Staked
                      </Text>
                      <styled.img
                        src="/tokens/LVMON.svg"
                        width="14px"
                        height="14px"
                      />
                      <Text
                        textStyle="text.xsmall"
                        color="greyWhite.lightGrey4"
                        whiteSpace="nowrap"
                      >
                        LVMON
                      </Text>
                    </HStack>
                    <Amount
                      value={stakedAmount}
                      fontSize="24px"
                      fontWeight="bold"
                      color="white"
                      whiteSpace="nowrap"
                      opts={{ displayDecimals: 2, autoKmb: false }}
                    />
                  </VStack>

                  <VStack alignItems="flex-start" gap={0}>
                    <Text
                      textStyle="text.xsmall"
                      color="greyWhite.lightGrey4"
                      whiteSpace="nowrap"
                    >
                      APY
                    </Text>
                    <HStack alignItems="baseline" gap={1}>
                      <Text
                        color="#D4F82D"
                        fontSize="38px"
                        lineHeight="38px"
                        fontWeight="bold"
                        whiteSpace="nowrap"
                      >
                        {formattedApy}
                      </Text>
                      <Text color="#D4F82D" fontSize="22px" fontWeight="bold">
                        %
                      </Text>
                    </HStack>
                  </VStack>
                </Flex>

                {/* Pepe, bottom-right.
                    Explicit width (matches the 895:659 svg ratio) so the
                    image keeps its size even before it has loaded — otherwise
                    width:auto collapses to 0 and the pepe is missing from a
                    quickly-triggered export. */}
                <styled.img
                  src="/images/share-stake-pepe.svg"
                  position="absolute"
                  right="20px"
                  top="50%"
                  transform="translateY(-50%)"
                  width="206px"
                  height="152px"
                  pointerEvents="none"
                />

                {/* Copied indicator */}
                <styled.div
                  data-ignore-capture="true"
                  position="absolute"
                  top="12px"
                  right="12px"
                  display={copied ? 'block' : 'none'}
                  bg="#19191e"
                  color="greyWhite.lightGrey1"
                  border="1px solid"
                  borderColor="greyWhite.outline"
                  px="10px"
                  py="6px"
                  borderRadius="8px"
                  textStyle="text.small"
                  pointerEvents="none"
                >
                  Copied to clipboard
                </styled.div>
              </styled.div>

              {/* Hover overlay */}
              <Flex
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                alignItems="center"
                justifyContent="center"
                bg="rgba(0,0,0,0.3)"
                opacity={0}
                transition="opacity 0.2s"
                _groupHover={{ opacity: { base: 0, md: 1 } }}
                pointerEvents="none"
              >
                <HStack
                  bg="rgba(0,0,0,0.7)"
                  px={3}
                  py={2}
                  borderRadius="8px"
                  gap={2}
                  backdropFilter="blur(4px)"
                >
                  <Icon boxSize="16px" color="white">
                    <Copy />
                  </Icon>
                  <Text
                    textStyle="text.small"
                    color="white"
                    fontWeight="medium"
                  >
                    Click to Copy
                  </Text>
                </HStack>
              </Flex>
            </styled.div>

            <ShareIdentityControls identity={identity} />

            <VStack px={6} alignItems="flex-start" gap={2}>
              <Text textStyle="headings.small" color="greyWhite.lightGrey3">
                Customize your text
              </Text>
              <Textarea
                value={shareText}
                color="greyWhite.lightGrey3"
                borderRadius="8px"
                border="1px solid"
                borderColor="greyWhite.outline"
                bg="#19191e"
                onChange={(e) => setShareText(e.target.value)}
                placeholder="Add a caption"
                rows={2}
                outline="none"
                boxShadow="none"
                _focusVisible={{
                  borderColor: 'greyWhite.outline',
                  boxShadow: 'none',
                  outline: 'none',
                }}
              />
            </VStack>

            <VStack px={6} alignItems="flex-start" gap={1}>
              <Text textStyle="text.small" color="greyWhite.lightGrey3">
                {canShare
                  ? "Tip: Tap 'Save Image' to save the card, then attach it to your post."
                  : "Tip: 'Share on X' automatically copies the image. Just paste (Ctrl+V) in your tweet."}
              </Text>
            </VStack>

            <Stack
              p={5}
              pt={0}
              gap={3}
              direction={{ base: 'column', md: 'row' }}
            >
              <Button onClick={onShareX} variant="primary" size="big" w="full">
                <HStack gap={2}>
                  <styled.img
                    src="/images/x-black.svg"
                    width="18px"
                    height="18px"
                  />
                  <span>Share on X</span>
                </HStack>
              </Button>

              <Button
                onClick={onCopyImage}
                loading={imageBusy}
                disabled={imageBusy}
                variant="secondary"
                size="big"
                w="full"
                border="1px solid" borderColor="v2.long.border"
                color="#FFD600"
                _hover={{
                  borderColor: '#FFD600',
                  color: '#FFD600',
                  bg: 'rgba(255,214,0,0.1)',
                }}
              >
                <HStack gap={2}>
                  <Icon boxSize="5" color="#FFD600">
                    <Copy />
                  </Icon>
                  <span>{canShare ? 'Save Image' : 'Copy Image'}</span>
                </HStack>
              </Button>

              <Button
                onClick={onCopyLink}
                variant="secondary"
                size="big"
                w="full"
                border="1px solid" borderColor="v2.long.border"
                color="#FFD600"
                _hover={{
                  borderColor: '#FFD600',
                  color: '#FFD600',
                  bg: 'rgba(255,214,0,0.1)',
                }}
              >
                <HStack gap={2}>
                  <Icon boxSize="5" color="#FFD600">
                    <ShareLink />
                  </Icon>
                  <span>Copy Link</span>
                </HStack>
              </Button>
            </Stack>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
